import type { HttpRequestConfig } from '../lib/utils/httpClient';
import type { AcsTokenResponse } from './AcsChatConfig';

// ============================================================================
// ACS Chat API Service  (factory pattern – same as Zoom service)
// ============================================================================
//
// Each portal passes its own httpRequest wrapper so the correct base URL
// and auth headers are used automatically.
//
// Usage in any portal:
//
//   import { createAcsChatApiService } from '@qler-view/shared_lib/src/shared/Chat/AcsChatApiService';
//   import { httpRequest } from '../../shared/lib/utils/httpClient';
//
//   const acsChatApi = createAcsChatApiService(httpRequest);
//
//   // 1. On login – get ACS token for current user
//   const { token, userId, threadId } = await acsChatApi.getAcsChatToken(appUserId);
//
//   // 2. On consult open – get thread ID for that consult
//   const { threadId } = await acsChatApi.getConsultChatThread(consultId);
//
// ============================================================================

export type HttpRequestFn = <T>(config: HttpRequestConfig) => Promise<T>;

/** Response from backend when fetching the chat thread for a consult. */
export interface ConsultChatThreadResponse {
  threadId: string;
  consultId: number;
  participants?: string;
}

export const createAcsChatApiService = (httpRequest: HttpRequestFn) => ({
  /**
   * Get ACS chat token for the current user.
   * Call this once on login / app init.
   * Backend creates the ACS user (if needed) and returns a token with chat scope.
   */
  getAcsChatToken: async (
    userId: number,
    portalType: string
  ): Promise<AcsTokenResponse> => {
    const response = await httpRequest<AcsTokenResponse>({
      url: `/integration/acs/chat/token`,
      method: 'GET',
      headers: {
        userId: userId.toString(),
        portalType: portalType,
      },
      apiBaseType: 'INTEGRATION',
    });
    return response;
  },

  /**
   * Get the chat thread ID for a specific consult.
   * Call this when the user opens a consult's chat.
   * Backend creates the thread (if it doesn't exist) and adds participants.
   */
  getConsultChatThread: async (
    consultId: number,
    userId: number,
    portalType: string
  ): Promise<ConsultChatThreadResponse> => {
    const response = await httpRequest<ConsultChatThreadResponse>({
      url: `/integration/acs/chat/thread?consultId=${consultId}`,
      method: 'GET',
      headers: {
        userId: userId.toString(),
        portalType: portalType,
      },
      apiBaseType: 'INTEGRATION',
    });
    return response;
  },
});
